import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { userService } from '../../services/userService';
import { Loader2 } from 'lucide-react';
import { dispatchAuthRequired } from '../../utils/authPrompt';

interface FollowButtonProps {
    userId: string;
    className?: string;
    onStateChange?: (isFollowing: boolean) => void;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
    userId,
    className = '',
    onStateChange
}) => {
    const [hasSession, setHasSession] = useState(() => Boolean(localStorage.getItem('token')));

    useEffect(() => {
        const syncSession = () => {
            setHasSession(Boolean(localStorage.getItem('token')));
        };

        window.addEventListener('auth-change', syncSession);
        window.addEventListener('storage', syncSession);

        return () => {
            window.removeEventListener('auth-change', syncSession);
            window.removeEventListener('storage', syncSession);
        };
    }, []);

    const { data, isLoading, mutate } = useSWR(
        hasSession ? `follow-status-${userId}` : null,
        async () => {
            const res = await userService.getFollowingStatus(userId, { suppressAuthPrompt: true });
            return res.isFollowing;
        },
        { revalidateOnFocus: false, dedupingInterval: 30000 }
    );

    const isFollowing = hasSession ? (data ?? false) : false;
    const [toggling, setToggling] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const handleToggleFollow = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (toggling || isLoading) return;
        if (!hasSession) {
            dispatchAuthRequired({ reason: 'follow-action' });
            return;
        }

        const previousState = isFollowing;
        // Optimistic update
        await mutate(!previousState, false);
        setToggling(true);

        try {
            const response = previousState
                ? await userService.unfollowUser(userId)
                : await userService.followUser(userId);

            if (!response.success) {
                throw new Error('关注状态更新失败');
            }

            if (previousState) {
                onStateChange?.(false);
            } else {
                onStateChange?.(true);
            }
        } catch (error) {
            console.error("Follow toggle failed", error);
            await mutate(previousState, false);
        } finally {
            setToggling(false);
        }
    };

    const busy = isLoading || toggling;

    return (
        <button
            onClick={handleToggleFollow}
            disabled={busy}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`
                px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center justify-center min-w-[80px]
                ${busy ? 'opacity-80 cursor-wait' : ''}
                ${isFollowing
                    ? isHovered
                        ? 'bg-red-50 text-red-600 border border-red-200'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                    : 'bg-black text-white hover:bg-gray-800 border border-black'
                }
                ${className}
            `}
        >
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            {!busy && isFollowing && isHovered && '取消关注'}
            {!busy && isFollowing && !isHovered && '已关注'}
            {!busy && !isFollowing && '+ 关注'}
        </button>
    );
};
